import type { CaseStudy } from '../../types/content'

export const deshmukhRao: CaseStudy = {
  slug: 'deshmukh-rao',
  summary:
    'A self-initiated concept. We invented a six-partner commercial firm with twelve years of results and a website that reads like a brochure for any firm anywhere, then turned its case history into the argument for hiring it.',
  overview: {
    industry: 'Concept · Commercial law practice',
    location: 'Concept · Pune, Maharashtra',
    timeline: 'Self-initiated concept, 2026',
    services: ['Content strategy', 'Website design', 'Webflow design system', 'Prototype'],
  },
  problem: {
    heading: 'The problem',
    paragraphs: [
      'This is concept work. No firm commissioned it and Deshmukh & Rao Associates does not exist. We built it because professional-services sites fail in the same way almost everywhere, and a concept lets us show the argument without redacting the evidence.',
      'The premise: a firm that wins work by reputation. A founder hears the name from another founder, looks the firm up, and either books a call or does not. The website is the only part of that sequence nobody has designed.',
      'It says what every firm says. Practice areas listed alphabetically, partner biographies written in the third person, a stock photograph of a courthouse the firm has never appeared in, and a contact form that asks for a matter type before asking for a name. Twelve years of results exist in the scenario — corporate restructurings, shareholder disputes, two NCLT matters and an acquisition above ₹ 350 crore — and not one of them is mentioned anywhere. The predictable consequence is that every enquiry arrives cold, and the first half of every call is spent establishing credibility the site should have established already.',
    ],
  },
  research: {
    heading: 'Assumptions and discovery',
    paragraphs: [
      'There is no client data here, so we are explicit about what is assumption and what is design. What follows are the premises the work is built on, drawn from how professional-services buying tends to behave rather than from research we did not conduct.',
      'The central assumption is that a prospective client asks three questions in some order — have you handled something like mine, who exactly will do the work, and what happens in the first two weeks — and that a conventional firm site answers none of them.',
      'The second assumption is that the bar in this category is low. We modelled a credibility audit of eleven comparable firms scored against those three questions and assumed most fail all three, which makes the opportunity structural rather than stylistic: a firm willing to publish its actual work would look measurably more credible than the field.',
      'The third is that the raw material already exists but is unpublished. We built an inventory of 46 matters that could be described without breaching confidence, sorted by sector, size and outcome, and treated it as the spine of the site. Finally we assumed the enquiry form is a leak rather than a filter, with a handful of fields doing most of the damage. A real engagement would test each of these in week one; here they are the brief.',
    ],
  },
  artifacts: [
    {
      kind: 'inventory',
      title: 'Matter inventory',
      caption: 'Modelled: 46 publishable matters scored by sector, size and outcome',
    },
    {
      kind: 'audit',
      title: 'Competitor credibility audit',
      caption: 'Eleven firms scored against the three questions we assume clients ask',
    },
    {
      kind: 'flow',
      title: 'Enquiry flow analysis',
      caption: 'The form fields we expect to cause most of the abandonment',
    },
  ],
  strategy: {
    heading: 'Strategy and decisions',
    paragraphs: [
      'The strategy was to stop describing the firm and start showing its work. If a visitor can see three matters that resemble their own situation, the biography and the practice-area page become supporting material rather than the whole argument.',
      'Structurally that meant inverting the site. Case history moved to the top level with its own filterable index. Practice areas became routes into that index rather than destinations. Partner pages were rewritten in the first person and stripped to what a client needs: what I work on, what I have done, who I answer the phone to.',
      'We also made the process visible. A short, honest description of the first two weeks of an engagement — who you meet, what you sign, when you get an estimate — replaced the reassurance paragraph nobody reads.',
    ],
  },
  decisions: [
    {
      decision: 'Published 46 matters instead of six testimonials',
      reasoning:
        'Testimonials are unfalsifiable and everyone has them. Redacted matter summaries with sector, size and outcome are checkable, and checkable is what buys trust in law.',
    },
    {
      decision: 'Rewrote partner biographies in the first person',
      reasoning:
        'Clients hire a person, not a practice. Third-person biography is a convention that exists to protect the firm, and it costs the reader the sense of who will pick up the phone.',
    },
    {
      decision: 'Cut the enquiry form from nine fields to three',
      reasoning:
        'Matter type, budget band and referral source are all guessed by the sender and corrected by the firm anyway. Name, email and one open box should outperform the qualification the form pretends to do.',
    },
    {
      decision: 'Declined an individual employment-law landing page',
      reasoning:
        'It would generate volume a commercial firm does not want. Recommending against the traffic is part of the design decision.',
    },
  ],
  design: {
    heading: 'Design direction',
    paragraphs: [
      'The design brief was authority without theatre. No gavels, no columns, no navy-and-gold. The page sits on a warm white ground with a single ink for type and hairline rules doing all the structural work, which suits a firm whose product is precision.',
      'Because the site is now mostly reading, typography carries almost the entire design. A restrained serif sets matter titles and pull quotes at generous size; a neutral sans handles metadata, filters and navigation. Measure is capped so no paragraph runs past a comfortable line length, and matter summaries are set as a two-column record — facts on the left, outcome on the right — so the page can be scanned as evidence rather than read as prose.',
      'The component set is small and text-first: a matter record, a filter row, a partner card, a process timeline and an enquiry panel. Everything is designed to be maintained by the partners themselves in a CMS, which is the condition the concept sets for itself.',
    ],
  },
  typefaces: [
    { name: 'Instrument Serif', role: 'Matter titles and pull quotes' },
    { name: 'Inter Tight', role: 'Body copy, metadata and navigation' },
  ],
  palette: [
    { name: 'Warm white', hex: '#FBFAF7' },
    { name: 'Ink', hex: '#14161A' },
    { name: 'Slate accent', hex: '#3D5A73' },
    { name: 'Hairline', hex: '#E3E1DA' },
  ],
  components: [
    'Matter record',
    'Sector and size filter row',
    'Partner card',
    'First two weeks timeline',
    'Three-field enquiry panel',
  ],
  mockupCaption: 'Home, case history index and matter record, desktop and mobile',
  before: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/5b7e7b8d-7548-4636-8180-83ef17d013d0.jpg',
    alt: 'The starting point in the concept: a navy and gold layout with a courthouse photograph and dense paragraphs',
  },
  results: {
    isProjected: true,
    intro:
      'Nothing here has been measured in market. These are the outcomes the design was built to hit, stated as targets so they can be argued with.',
    metrics: [
      { value: 'Warm', label: 'Target: enquiries that arrive already convinced' },
      { value: 'On remit', label: 'Target: fewer enquiries outside the firm’s practice' },
      { value: '46', label: 'Target: matters published and partner-maintained' },
      { value: '3 fields', label: 'Target enquiry form, down from nine' },
    ],
    note:
      'We would judge this successful if first calls started at the second question rather than the first — credibility established before the meeting, so the meeting is about the work. That is the goal, not a measured result.',
  },
}
